import React from "react";
import { Link } from "react-router-dom";
import { BadgeCheck, PlayCircle, ScanLine } from "lucide-react";
import { useCurrency } from "../lib/currency";
import { motion, rise, staggerParent, VIEWPORT, EASE_OUT } from "../lib/motion";
import { GRAHA_DETAIL, INTENT_NAV, STONE_DEVA, TYPE_DEVA } from "../content/nav";

const RARE_ORIGINS = ["Kashmir", "Burma", "Myanmar", "Colombia", "Basra"];

/** Badges are derived from catalogue facts only — never a sales claim we can't prove. */
function statusBadge(product) {
  if (product.status === "sold") return { label: "Sold", tone: "bg-ink text-wash" };
  const g = product.gemstone;
  const r = product.rudraksha;
  if (g && RARE_ORIGINS.some((o) => (g.origin || "").includes(o))) {
    return { label: "Rare origin", tone: "bg-deep text-wash" };
  }
  if (g && g.treatment === "Unheated") return { label: "Unheated", tone: "bg-verify text-white dark:text-[#08130c]" };
  if (r && (product.origin === "nepal" || r.origin === "nepal")) {
    return { label: "Nepal", tone: "bg-deep text-wash" };
  }
  if (product.stock_qty === 1 && product.status === "available") {
    return { label: "Last one", tone: "bg-gold text-white dark:text-[#141310]" };
  }
  return null;
}

function devaLine(product) {
  if (product.stone_slug && STONE_DEVA[product.stone_slug]) {
    const roman = (product.title.match(/\(([^)]+)\)/) || [])[1];
    return { deva: STONE_DEVA[product.stone_slug], roman };
  }
  if (product.type === "rudraksha") {
    return { deva: `${product.mukhi ? `${product.mukhi} मुखी ` : ""}रुद्राक्ष`, roman: "Rudraksha" };
  }
  const deva = TYPE_DEVA[product.type];
  return deva ? { deva, roman: null } : null;
}

function traditionLine(product) {
  const intent = (product.intents || [])[0];
  const label = INTENT_NAV.find((i) => i.slug === intent)?.label;
  if (label) return `Traditionally worn for ${label.toLowerCase()}`;
  const graha = GRAHA_DETAIL.find((g) => g.slug === product.graha);
  return graha ? `Associated with ${graha.name} in the tradition` : "Sold as a traditional item";
}

export function ProductCard({ product, index = 0, priority = false }) {
  const { format } = useCurrency();
  const g = product.gemstone;
  const r = product.rudraksha;
  const cert = product.certificate;
  const image = product.images?.[0];
  const xray = product.images?.find((i) => i.type === "xray");
  const badge = statusBadge(product);
  const deva = devaLine(product);
  const graha = GRAHA_DETAIL.find((x) => x.slug === product.graha);

  return (
    <motion.div variants={rise} className="h-full">
      <Link
        to={`/product/${product.slug}`}
        data-testid={`product-card-${product.slug}`}
        className="group card-s elev flex h-full flex-col transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:border-gold hover:shadow-[0_18px_40px_-24px_rgba(27,26,23,0.28)]"
      >
        <div className="relative h-[300px] overflow-hidden bg-wash">
          <motion.img
            src={image?.url}
            alt={image?.alt || product.title}
            loading={priority || index < 4 ? "eager" : "lazy"}
            className="h-full w-full object-cover"
            initial={{ scale: 1 }}
            whileHover={{ scale: 1.045 }}
            transition={{ duration: 0.9, ease: EASE_OUT }}
          />

          {badge && (
            <span
              className={`badge-status absolute left-3 top-3 ${badge.tone}`}
              data-testid={`card-badge-${product.slug}`}
            >
              {badge.label}
            </span>
          )}

          {cert?.is_third_party && (
            <span
              className="ui absolute right-3 top-3 inline-flex items-center gap-1 border border-verify/60 bg-surface/95 px-2 py-1 text-[10px] font-medium uppercase tracking-[0.12em] text-verify"
              data-testid={`card-certified-${product.slug}`}
            >
              <BadgeCheck className="h-3 w-3" aria-hidden="true" /> Certified
            </span>
          )}

          {product.image_shared && !xray && (
            <span
              className="ui absolute bottom-3 left-3 inline-flex items-center gap-1 bg-ink/78 backdrop-blur px-2 py-1 text-[10px] font-medium uppercase tracking-[0.14em] text-wash"
              data-testid={`card-typephoto-${product.slug}`}
              title="Representative photograph of this stone type — the photograph of your actual stone is added before dispatch."
            >
              Type photo
            </span>
          )}
          {product.representative_image && !product.image_shared && !xray && (
            <span
              className="ui absolute bottom-3 left-3 inline-flex items-center gap-1 bg-ink/78 backdrop-blur px-2 py-1 text-[10px] font-medium uppercase tracking-[0.14em] text-wash"
              data-testid={`card-representative-${product.slug}`}
              title="Representative image — your piece will vary."
            >
              Representative image
            </span>
          )}
          {xray && (
            <span className="ui absolute bottom-3 left-3 inline-flex items-center gap-1 bg-deep/90 px-2 py-1 text-[10px] uppercase tracking-[0.12em] text-wash">
              <ScanLine className="h-3 w-3" aria-hidden="true" /> X-ray
            </span>
          )}
          {product.video_url && (
            <PlayCircle className="absolute bottom-3 right-3 h-6 w-6 text-white/90 drop-shadow" aria-hidden="true" />
          )}
          <span className="pointer-events-none absolute inset-x-0 bottom-0 h-px origin-left scale-x-0 bg-gold transition-transform duration-500 ease-out group-hover:scale-x-100" />
        </div>

        <div className="flex flex-1 flex-col p-5">
          {product.representative_image && !product.image_shared && (
            <p
              className="ui text-[10.5px] leading-snug text-dim"
              data-testid={`card-representative-caption-${product.slug}`}
            >
              Representative image — your piece will vary.
            </p>
          )}
          <p className={`ui text-[10px] uppercase tracking-[0.18em] text-dim ${product.representative_image && !product.image_shared ? "mt-2" : ""}`}>
            {product.type === "gemstone"
              ? "Gemstone"
              : product.type === "semi-precious"
              ? "Semi-precious"
              : product.type}
            {graha ? ` · ${graha.name}` : ""}
          </p>

          <h3 className="mt-2.5 text-base leading-tight transition-colors duration-200 group-hover:text-gold">
            {product.title.replace(/\s*\([^)]*\)\s*$/, "")}
          </h3>

          {deva && (
            <p className="mt-1 text-sm text-gold">
              <span className="deva">{deva.deva}</span>
              {deva.roman && <span className="ui ml-1.5 italic opacity-70">{deva.roman}</span>}
            </p>
          )}

          {product.type !== "semi-precious" && (
            <p className="mt-2.5 text-[13px] leading-[1.6] text-dim">{traditionLine(product)}</p>
          )}

          <p className="num mt-3 text-xs text-ink">
            {g && `${g.carat} ct / ${g.ratti} ratti · ${g.origin} · ${g.treatment}`}
            {r && `${r.mukhi} mukhi · ${r.diameter_mm} mm`}
            {!g && !r && product.subtitle}
          </p>
          {cert && (
            <p className="ui mt-1 text-[11px] text-dim">
              {cert.lab} <span className="num">{cert.report_number}</span>
            </p>
          )}

          <div className="mt-auto flex items-baseline justify-between gap-3 border-t border-rule pt-4">
            {product.price_inr == null ? (
              <p
                className="ui text-[13px] font-medium uppercase tracking-[0.1em] text-gold"
                data-testid={`card-price-on-request-${product.slug}`}
              >
                Price on request
              </p>
            ) : (
              <p className="num text-[22px] font-medium leading-none text-deep">
                {format(product.price_inr)}
              </p>
            )}
            {product.price_per_carat_inr ? (
              <p className="num text-[11px] text-dim">{format(product.price_per_carat_inr)} / ct</p>
            ) : product.price_inr == null && product.stock_display === "in-stock" ? (
              <p className="ui text-[11px] text-dim">In stock</p>
            ) : (
              <p className="ui text-[11px] text-dim">Tax included</p>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export function ProductGrid({ products, testId = "product-grid", className = "" }) {
  return (
    <motion.div
      data-testid={testId}
      variants={staggerParent(0.06)}
      initial="hidden"
      whileInView="show"
      viewport={VIEWPORT}
      className={`grid grid-cols-1 gap-5 sm:grid-cols-[repeat(auto-fill,minmax(300px,1fr))] ${className}`}
    >
      {products.map((p, i) => (
        <ProductCard key={p.slug} product={p} index={i} />
      ))}
    </motion.div>
  );
}

export function ProductGridSkeleton({ count = 4, testId = "product-grid-skeleton" }) {
  return (
    <div
      data-testid={testId}
      aria-busy="true"
      className="grid grid-cols-1 gap-5 sm:grid-cols-[repeat(auto-fill,minmax(300px,1fr))]"
    >
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="card-s">
          <div className="skeleton aspect-square w-full" />
          <div className="p-5">
            <div className="skeleton h-3 w-1/3" />
            <div className="skeleton mt-3 h-4 w-3/4" />
            <div className="skeleton mt-2 h-3 w-1/2" />
            <div className="skeleton mt-5 h-5 w-24" />
          </div>
        </div>
      ))}
    </div>
  );
}
